'use client';

import { useEffect, useState } from 'react';
import React from 'react';
import styled, { keyframes } from 'styled-components';
import Image from 'next/image';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import FAQSection from '@/components/FAQSection';
import SupportSection from '@/components/SupportSection';
import AddToCart from '@/components/AddToCart';
import ProductImage from '@/components/ProductImage';
import { GetData } from '@/utils/request';
import { statics, SHOPIFY_CONFIG } from '@/data/store';
import { COLORS } from '@/constants/colors';
import { crossReferenceBrandFaqs } from '@/data/faqData';
import { formatMakeName } from '@/utils/formatters';

const TEXT_DARK = '#333';
const BUTTON_HEIGHT_DESKTOP = 40;

interface Company {
  id: number;
  title: string;
  compatitor_products?: CompetitorProduct[];
  com_products?: CompetitorProduct[];
}

interface CompetitorProduct {
  id: number;
  competitor_part_number: string;
  product_id: number;
}

interface GreenFilterProduct {
  id: number;
  title?: string;
  gfu_part_num?: string;
  buy_url: string;
  images?: Array<{ link: string }>;
}

interface ShopifyPrice {
  price: string;
  compareAtPrice: string | null;
}

interface CrossReferenceBrandPageProps {
  initialCompanies?: Company[];
}

export default function CrossReferenceBrandPage({
  initialCompanies = [],
}: CrossReferenceBrandPageProps) {
  const [companies, setCompanies] = useState<Company[]>(initialCompanies);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<CompetitorProduct | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<CompetitorProduct[]>([]);
  const [selectedGreenFilters, setSelectedGreenFilters] = useState<GreenFilterProduct | null>(null);
  const [showModel, setShowModel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shopifyPrice, setShopifyPrice] = useState<ShopifyPrice | null>(null);

  useEffect(() => {
    if (initialCompanies.length === 0) {
      getCompanies();
    }
  }, []);

  useEffect(() => {
    if (selectedFilter) {
      getProductById();
    }
  }, [selectedFilter]);

  useEffect(() => {
    if (selectedGreenFilters) {
      setShowModel(true);
    }
    if (selectedCompany) {
      getFilterCodes();
    }
  }, [selectedCompany]);

  // Fetch Shopify price for the product
  useEffect(() => {
    if (!selectedGreenFilters?.buy_url) {
      setShopifyPrice(null);
      return;
    }
    const gid = (id: string) =>
      typeof id === 'string' && id.startsWith('gid://') ? id : `gid://shopify/Product/${id}`;
    const url = `https://${SHOPIFY_CONFIG.domain}/api/${SHOPIFY_CONFIG.apiVersion}/graphql.json`;
    const token = SHOPIFY_CONFIG.apiKey;

    const fetchPrice = async () => {
      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token,
          },
          body: JSON.stringify({
            query: `query getProductPrice($id: ID!) { product(id: $id) { variants(first: 1) { nodes { price { amount } compareAtPrice { amount } } } } }`,
            variables: { id: gid(selectedGreenFilters.buy_url) },
          }),
        });
        const json = await res.json();
        const nodes = json?.data?.product?.variants?.nodes;
        if (nodes?.length) {
          const price = nodes[0].price?.amount;
          const compareAtPrice = nodes[0].compareAtPrice?.amount;
          if (price != null) {
            setShopifyPrice({ price, compareAtPrice: compareAtPrice || null });
          }
        }
      } catch (e) {
        console.warn('Shopify price fetch failed', e);
      }
    };
    fetchPrice();
  }, [selectedGreenFilters]);

  const getCompanies = async () => {
    setLoading(true);
    let tableDataT: Company[] = [];
    const response = await GetData(`${statics.BaseUrl}/competitor?pageSize=200`, 200, null);
    if (response.ResponseCode === 'Success') {
      tableDataT = response?.data?.rows;
    }
    setCompanies(tableDataT);
    setLoading(false);
  };

  const getFilterCodes = async () => {
    let tableDataT: CompetitorProduct[] = [];
    const response = await GetData(
      `${statics.BaseUrl}/competitor?id=${selectedCompany?.id}`,
      200,
      null
    );
    if (response.ResponseCode === 'Success') {
      tableDataT = response?.data?.com_products;
    }
    setSelectedFilters(tableDataT);
  };

  const getProductById = async () => {
    let tableDataT: GreenFilterProduct[] = [];

    const response = await GetData(
      `${statics.BaseUrl}/product?id=${selectedFilter?.product_id}`,
      200,
      null
    );
    if (response.ResponseCode === 'Success') {
      tableDataT = response?.data?.rows;
    }

    setSelectedGreenFilters(tableDataT[0]);
    setShowModel(true);
  };

  const onChangeCompanyHandler = (id: string) => {
    setShowModel(false);
    setSelectedCompany(null);
    setSelectedFilter(null);
    setShopifyPrice(null);
    const selectedCompanyData = companies.filter((item) => item.id === parseInt(id));

    if (selectedCompanyData.length) {
      setSelectedCompany(selectedCompanyData[0]);
    }
  };

  const onChangeFilterHandler = (id: string) => {
    setShowModel(false);
    setShopifyPrice(null);
    const selectedFilterData = selectedCompany?.compatitor_products?.filter(
      (item) => item.id === parseInt(id)
    );
    if (selectedFilterData && selectedFilterData.length) {
      setSelectedFilter(selectedFilterData[0]);
    }
  };

  const selectSx = {
    fontFamily: 'Lato, sans-serif',
    minHeight: `${BUTTON_HEIGHT_DESKTOP}px`,
    height: `${BUTTON_HEIGHT_DESKTOP}px`,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#d0d0d0',
      borderRadius: '4px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#999',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: COLORS.brandGreen,
      borderWidth: 2,
    },
    '& .MuiSelect-select': {
      padding: '0 14px',
      minHeight: `${BUTTON_HEIGHT_DESKTOP}px`,
      height: `${BUTTON_HEIGHT_DESKTOP}px`,
      display: 'flex',
      alignItems: 'center',
      fontSize: '13px',
      fontWeight: 600,
      lineHeight: `${BUTTON_HEIGHT_DESKTOP}px`,
    },
    '& .MuiInputLabel-root': {
      fontSize: '13px',
      top: '-4px',
      '&.MuiInputLabel-shrink': {
        top: '0',
      },
    },
  } as const;

  return (
    <>
      <Header handleClickIndex={() => {}} />
      <div className="header-gap-creator"></div>

      {loading && (
        <LoadingOverlay>
          <Spinner />
        </LoadingOverlay>
      )}

      <main>
      <Breadcrumbs items={[{ label: 'Cross Reference' }, { label: 'Other Brand to Green Filter' }]} />
      <Section>
        <Container>
          <HeadingBlock>
            <Headline>Air Filter Cross Reference</Headline>
            <Accent />
            <IntroText>
              Use the Green Filter air filter cross-reference tool to find a compatible Green
              Filter replacement for another brand's filter. Select the company, enter the filter
              code, and compare available Green Filter products for your application.
            </IntroText>
          </HeadingBlock>

          <ContentBlock>
            <SectionTitle>Find a Green Filter Interchange</SectionTitle>
            <SectionDescription>
              Our filter conversion chart helps compare aftermarket air filter numbers from other
              brands with available Green Filter options. Use this tool for K&N air filter
              cross-reference, FRAM air filter cross-reference, WIX air filter cross-reference, and
              other compatible filter searches.
            </SectionDescription>
          </ContentBlock>

          <ContentGrid>
            <ImageContainer>
              <Image src="/assets/filters.webp" alt="Green Filter Products" width={520} height={317} />
            </ImageContainer>

            <FormCard>
              <FormInstruction>
                Select a company and enter the filter code to search for matching Green Filter
                products.
              </FormInstruction>
              <FormInnerCard>
                <FormRow>
                  <SelectWrapper>
                    <label htmlFor="company-select">Company</label>
                    <StyledSelect
                      id="company-select"
                      value={selectedCompany?.id?.toString() || ''}
                      onChange={(e) => onChangeCompanyHandler(e.target.value)}
                    >
                      <option value="">Select</option>
                      {companies &&
                        companies.map((item) => (
                          <option key={item.id} value={item.id.toString()}>
                            {formatMakeName(item.title)}
                          </option>
                        ))}
                    </StyledSelect>
                  </SelectWrapper>
                </FormRow>

                <FormRow>
                  <SelectWrapper>
                    <label htmlFor="filter-select">Filter Code</label>
                    <StyledSelect
                      id="filter-select"
                      value={selectedFilter?.id?.toString() || ''}
                      onChange={(e) => onChangeFilterHandler(e.target.value)}
                      disabled={!selectedCompany}
                    >
                      <option value="">Select</option>
                      {selectedFilters &&
                        selectedFilters.map((item) => (
                          <option key={item.id} value={item.id.toString()}>
                            {item.competitor_part_number}
                          </option>
                        ))}
                    </StyledSelect>
                  </SelectWrapper>
                </FormRow>
              </FormInnerCard>
            </FormCard>
          </ContentGrid>

          {showModel &&
            selectedGreenFilters &&
            (() => {
              const partTitle =
                selectedGreenFilters.title ||
                `Green Filter Part #${selectedGreenFilters.gfu_part_num || ''}`;

              return (
                <ResultSection>
                  <ProductCard>
                    <ProductCardImage>
                      <ProductImage
                        buyButtonId={selectedGreenFilters.buy_url}
                        id={`cross-ref-${selectedGreenFilters.id}`}
                      />
                    </ProductCardImage>

                    <ProductCardBody>
                      <ProductCardTitle>{partTitle}</ProductCardTitle>

                      <FitmentNote>
                        Replaces: {formatMakeName(selectedCompany?.title)}{' '}
                        {selectedFilter?.competitor_part_number}
                      </FitmentNote>

                      {shopifyPrice && (
                        <PriceRow>
                          <span className="price-current">
                            ${Number(shopifyPrice.price).toFixed(2)}
                          </span>
                          {shopifyPrice.compareAtPrice && (
                            <span className="price-compare">
                              ${Number(shopifyPrice.compareAtPrice).toFixed(2)}
                            </span>
                          )}
                        </PriceRow>
                      )}

                      <ButtonGroup>
                        <ViewDetailsButton
                          href={`/store/filter/${selectedGreenFilters.gfu_part_num}`}
                        >
                          View Product Details
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden="true"
                            style={{ marginLeft: 4 }}
                          >
                            <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                          </svg>
                        </ViewDetailsButton>
                        <PrimaryButtonWrap>
                          <AddToCart
                            buyButtonId={selectedGreenFilters?.buy_url}
                            id={selectedGreenFilters?.id?.toString()}
                            background={COLORS.brandGreen}
                            color="#fff"
                            hidePrice={true}
                          />
                        </PrimaryButtonWrap>
                      </ButtonGroup>
                    </ProductCardBody>
                  </ProductCard>
                </ResultSection>
              );
            })()}
        </Container>
      </Section>

      <FAQSection faqs={crossReferenceBrandFaqs} title="FAQs About Air Filter Cross Reference" />

      <SupportSection
        text="Need help finding the right Green Filter replacement? Use our vehicle selector or contact our support team for expert assistance."
        primaryButtonText="Shop by Vehicle"
        primaryButtonLink="/shop-by-vehicle"
        secondaryButtonText="Contact Support"
        secondaryButtonLink="/contact-us"
      />
      </main>

      <Footer />
    </>
  );
}

// Styled Components

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  border: 6px solid rgba(0, 173, 35, 0.1);
  border-top: 6px solid ${COLORS.brandGreen};
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: ${spin} 1s linear infinite;
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  label {
    font-family: Lato, sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: ${TEXT_DARK};
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 12px 16px;
  font-family: Lato, sans-serif;
  font-size: 14px;
  color: ${TEXT_DARK};
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  outline: none;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: ${COLORS.brandGreen};
  }

  &:focus {
    border-color: ${COLORS.brandGreen};
    box-shadow: 0 0 0 3px rgba(0, 173, 35, 0.1);
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
    opacity: 0.6;
  }

  option {
    padding: 8px;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const Section = styled.section`
  background: #fafafa;
  padding: 80px 24px;
  @media (max-width: 767px) {
    padding: 48px 16px;
  }
`;

const Container = styled.div`
  max-width: 1140px;
  margin: 0 auto;
`;

const HeadingBlock = styled.div`
  max-width: 640px;
  margin: 0 auto 56px;
  text-align: center;
  @media (max-width: 767px) {
    margin-bottom: 40px;
  }
`;

const Headline = styled.h1`
  font-family: Lato, sans-serif;
  font-size: 32px;
  font-weight: 800;
  color: ${TEXT_DARK};
  margin: 0;
  line-height: 1.2;
  @media (max-width: 767px) {
    font-size: 24px;
  }
`;

const Accent = styled.div`
  width: 56px;
  height: 3px;
  background: ${COLORS.brandGreen};
  border-radius: 2px;
  margin: 14px auto 16px;
`;

const IntroText = styled.p`
  font-family: Lato, sans-serif;
  font-size: 16px;
  line-height: 1.6;
  color: #555;
  margin: 0;
  max-width: 720px;
  margin: 0 auto;
  @media (max-width: 767px) {
    font-size: 14px;
  }
`;

const ContentBlock = styled.div`
  max-width: 960px;
  margin: 0 auto 32px;
  text-align: center;
  @media (max-width: 767px) {
    margin-bottom: 24px;
  }
`;

const SectionTitle = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: ${TEXT_DARK};
  margin: 0 0 12px;
  line-height: 1.3;
  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const SectionDescription = styled.p`
  font-family: Lato, sans-serif;
  font-size: 15px;
  line-height: 1.6;
  color: #555;
  margin: 0;
  max-width: 800px;
  margin: 0 auto;
  @media (max-width: 767px) {
    font-size: 14px;
  }
`;

const FormInstruction = styled.p`
  font-family: Lato, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #666;
  margin: 0 0 16px;
  @media (max-width: 767px) {
    font-size: 13px;
  }
`;

const ContentGrid = styled.div`
  max-width: 960px;
  margin: 0 auto 40px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  padding: 32px;
  display: grid;
  grid-template-columns: 400px 1fr;
  gap: 40px;
  align-items: center;

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
    gap: 32px;
    padding: 28px;
  }

  @media (max-width: 767px) {
    padding: 24px 20px;
  }
`;

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #f8f8f8;
  border-radius: 8px;

  img {
    width: 100%;
    max-width: 100%;
    height: auto;
    object-fit: contain;
  }

  @media (max-width: 991px) {
    padding: 28px;
  }

  @media (max-width: 767px) {
    padding: 20px;
  }
`;

const FormCard = styled.div`
  /* Container for the form */
`;

const FormInnerCard = styled.div`
  background: #f8f8f8;
  border-radius: 8px;
  padding: 24px;
  border: 1px solid #ebebeb;
  @media (max-width: 767px) {
    padding: 20px;
  }
`;

const FormRow = styled.div`
  margin-bottom: 20px;
  &:last-of-type {
    margin-bottom: 0;
  }
  .MuiFormControl-root {
    width: 100%;
  }
  .MuiInputLabel-root {
    font-family: Lato, sans-serif;
  }
  .MuiInputLabel-root.Mui-focused {
    color: ${COLORS.brandGreen};
  }
`;

const ResultSection = styled.div`
  margin-top: 40px;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
`;

const ProductCard = styled.div`
  display: flex;
  align-items: stretch;
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const ProductCardImage = styled.div`
  flex-shrink: 0;
  width: 240px;
  min-height: 240px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;

  /* Override ProductImage default styles */
  & > div {
    margin: 0 !important;
    max-width: 100% !important;
  }

  img {
    width: 100%;
    height: auto;
    max-height: 200px !important;
    object-fit: contain;
  }

  @media (max-width: 767px) {
    width: 100%;
    min-height: 200px;
    padding: 20px;

    img {
      max-height: 180px !important;
    }
  }
`;

const ProductCardBody = styled.div`
  flex: 1;
  padding: 28px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-family: Lato, sans-serif;
  @media (max-width: 767px) {
    padding: 20px;
    gap: 16px;
  }
`;

const ProductCardTitle = styled.h3`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #333;
  line-height: 1.3;
  @media (max-width: 767px) {
    font-size: 20px;
  }
`;

const FitmentNote = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  color: #666;
  line-height: 1.5;
  @media (max-width: 767px) {
    font-size: 14px;
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin: 0;
  .price-current {
    font-size: 24px;
    font-weight: 700;
    color: #333;
  }
  .price-compare {
    font-size: 16px;
    font-weight: 400;
    color: #888;
    text-decoration: line-through;
  }
  @media (max-width: 767px) {
    .price-current {
      font-size: 20px;
    }
    .price-compare {
      font-size: 14px;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 12px;
  width: 100%;
  & > * {
    flex: 1;
    min-width: 0;
  }
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
    & > * {
      flex: none;
      width: 100%;
    }
  }
`;

const ViewDetailsButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 16px;
  height: ${BUTTON_HEIGHT_DESKTOP}px;
  min-height: ${BUTTON_HEIGHT_DESKTOP}px;
  box-sizing: border-box;
  background-color: transparent;
  color: ${COLORS.brandGreen} !important;
  font-family: Lato, sans-serif;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 4px;
  border: 2px solid ${COLORS.brandGreen};
  cursor: pointer;
  width: 100%;
  transition: background-color 0.2s, color 0.2s;
  &:hover {
    background-color: rgba(0, 173, 35, 0.08);
    color: ${COLORS.brandGreen} !important;
  }
  svg {
    width: 16px;
    height: 16px;
  }
`;

const PrimaryButtonWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${BUTTON_HEIGHT_DESKTOP}px;
  min-height: ${BUTTON_HEIGHT_DESKTOP}px;
  margin: 0;
  box-sizing: border-box;
  width: 100%;

  & > div {
    margin: 0 !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100% !important;
    height: ${BUTTON_HEIGHT_DESKTOP}px !important;
    min-height: ${BUTTON_HEIGHT_DESKTOP}px !important;
  }

  .shopify-buy__btn-wrapper,
  [data-element='product.buttonWrapper'] {
    margin-top: 0 !important;
    width: 100% !important;
  }

  .shopify-payment-button__button,
  .shopify-buy__btn {
    font-family: Lato, sans-serif !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    padding: 0 16px !important;
    border-radius: 4px !important;
    height: ${BUTTON_HEIGHT_DESKTOP}px !important;
    min-height: ${BUTTON_HEIGHT_DESKTOP}px !important;
    max-height: ${BUTTON_HEIGHT_DESKTOP}px !important;
    line-height: ${BUTTON_HEIGHT_DESKTOP}px !important;
    margin: 0 auto !important;
    width: 100% !important;
    box-sizing: border-box !important;
    text-align: center !important;
    background-color: ${COLORS.brandGreen} !important;
    color: #fff !important;
    border: none !important;
    cursor: pointer !important;
    transition: background-color 0.2s ease !important;
    text-transform: none !important;
    letter-spacing: normal !important;

    &:hover {
      background-color: #009c1f !important;
    }

    &:focus {
      background-color: #009c1f !important;
    }
  }
`;